import { authenticatedRequest } from './authApi';
import { REST_API_ORIGIN } from '../platform/endpoints';
import { localizedValue } from '../localization/presentationLanguage';
const feedError = (es, en) => localizedValue({ es, en });

function withParams(url, params) {
  if (!params) return url;
  const target = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  Object.entries(params).forEach(([key, value]) => {
    if (value != null) target.searchParams.set(key, String(value));
  });
  return target.toString();
}

const authenticatedAxios = {
  async get(url, config = {}) {
    const data = await authenticatedRequest(withParams(url, config.params), {
      method: 'GET', headers: config.headers, signal: config.signal, diagSource: 'feedApi',
    });
    return { data };
  },
  async post(url, body, config = {}) {
    const data = await authenticatedRequest(url, {
      method: 'POST', body, headers: config.headers, signal: config.signal, diagSource: 'feedApi',
    });
    return { data };
  },
  async delete(url, config = {}) {
    const data = await authenticatedRequest(url, {
      method: 'DELETE', headers: config.headers, signal: config.signal, diagSource: 'feedApi',
    });
    return { data };
  },
};

const API_BASE_URL = REST_API_ORIGIN;

const FEED_BASE = `${String(API_BASE_URL).replace(/\/$/, '')}/api/feed`;

function getFeedBaseUrl() {
  return FEED_BASE;
}

function readTokenFromStorage(storage) {
  if (!storage) return '';

  const directCandidates = ['token', 'authToken', 'accessToken', 'jwt', 'zodiacusToken'];
  for (const key of directCandidates) {
    const value = String(storage.getItem(key) || '').trim();
    if (value) return value;
  }

  const jsonCandidates = ['userInfo', 'user', 'auth', 'session'];
  for (const key of jsonCandidates) {
    const raw = String(storage.getItem(key) || '').trim();
    if (!raw) continue;

    try {
      const parsed = JSON.parse(raw);
      const nestedValue =
        parsed?.token ||
        parsed?.authToken ||
        parsed?.accessToken ||
        parsed?.jwt ||
        parsed?.userInfo?.token ||
        parsed?.data?.token ||
        '';

      if (String(nestedValue || '').trim()) {
        return String(nestedValue).trim();
      }
    } catch {
      // ignore malformed storage payloads
    }
  }

  return '';
}

function resolveAuthToken(explicitToken) {
  const safeExplicitToken = String(explicitToken || '').trim();
  if (safeExplicitToken) return safeExplicitToken;

  if (typeof window === 'undefined') return '';

  const fromLocalStorage = readTokenFromStorage(window.localStorage);
  if (fromLocalStorage) return fromLocalStorage;

  const fromSessionStorage = readTokenFromStorage(window.sessionStorage);
  if (fromSessionStorage) return fromSessionStorage;

  return '';
}

function getAuthHeaders(token) {
  const resolvedToken = resolveAuthToken(token);
  if (!resolvedToken) return {};
  return {
    Authorization: `Bearer ${resolvedToken}`,
  };
}

function normalizeId(value) {
  return String(value || '').trim();
}

function normalizeFeedMode(mode) {
  const raw = String(mode || '').trim().toLowerCase();

  if (raw === 'recientes') return 'recientes';
  if (raw === 'tu-signo' || raw === 'tu_signo') return 'tu-signo';
  return 'para-vos';
}

function getErrorMessage(error, fallback) {
  const payload = error?.response?.data ?? error?.payload;
  return (
    payload?.message ||
    payload?.error ||
    error?.message ||
    fallback ||
    feedError('Ocurrió un error inesperado.', 'An unexpected error occurred.')
  );
}

function getErrorFlags(error) {
  const payload = error?.response?.data ?? error?.payload;
  return Array.isArray(payload?.flags)
    ? payload.flags
    : [];
}

function getErrorStatus(error, fallback = 500) {
  return Number(error?.response?.status ?? error?.status ?? error?.statusCode ?? fallback);
}

function buildFeedPostUrl(postId, options = {}) {
  const safePostId = normalizeId(postId);
  if (!safePostId) return '';

  const providedBaseUrl = String(options?.baseUrl || '').trim();

  if (providedBaseUrl) {
    try {
      const url = new URL(providedBaseUrl);
      url.searchParams.set('post', safePostId);
      return url.toString();
    } catch {
      // ignore invalid base url and continue with browser fallback
    }
  }

  if (typeof window !== 'undefined' && window?.location?.href) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('post', safePostId);
      return url.toString();
    } catch {
      return '';
    }
  }

  return '';
}

async function getTimeline({ token, limit = 10, cursor = null, mode = 'para-vos' } = {}) {
  try {
    const safeMode = normalizeFeedMode(mode);
    const response = await authenticatedAxios.get(`${FEED_BASE}/timeline`, {
      headers: getAuthHeaders(token),
      params: {
        limit,
        mode: safeMode,
        ...(cursor ? { cursor } : {}),
      },
    });

    return {
      ok: !!response?.data?.ok,
      items: Array.isArray(response?.data?.items) ? response.data.items : [],
      nextCursor: response?.data?.nextCursor || null,
    };
  } catch (error) {
    const message = getErrorMessage(error, feedError('No se pudo cargar el feed.', 'The feed could not be loaded.'));
    const err = new Error(message);
    err.statusCode = getErrorStatus(error);
    err.flags = getErrorFlags(error);
    throw err;
  }
}

async function uploadFeedImage({ token, file } = {}) {
  try {
    if (!file) {
      const err = new Error(feedError('No se seleccionó ninguna imagen.', 'No image was selected.'));
      err.statusCode = 400;
      err.flags = [];
      throw err;
    }

    const formData = new FormData();
    formData.append('image', file);

    const resolvedToken = resolveAuthToken(token);

    const response = await authenticatedAxios.post(`${FEED_BASE}/uploads/image`, formData, {
      headers: {
        Authorization: `Bearer ${resolvedToken}`,
      },
      withCredentials: true,
    });

    return {
      ok: !!response?.data?.ok,
      upload: response?.data?.upload || null,
    };
  } catch (error) {
    const message = getErrorMessage(error, feedError('No se pudo subir la imagen.', 'The image could not be uploaded.'));
    const err = new Error(message);
    err.statusCode = getErrorStatus(error);
    err.flags = getErrorFlags(error);
    throw err;
  }
}

async function deleteFeedUpload({ token, uploadId } = {}) {
  const safeUploadId = normalizeId(uploadId);

  try {
    const response = await authenticatedAxios.delete(`${FEED_BASE}/uploads/${safeUploadId}`, {
      headers: getAuthHeaders(token),
    });

    return {
      ok: !!response?.data?.ok,
      uploadId: safeUploadId,
    };
  } catch (error) {
    const message = getErrorMessage(error, feedError('No se pudo eliminar la imagen.', 'The image could not be deleted.'));
    const err = new Error(message);
    err.statusCode = getErrorStatus(error);
    err.flags = getErrorFlags(error);
    throw err;
  }
}

async function createPost({ token, text, uploadId = null } = {}) {
  try {
    const response = await authenticatedAxios.post(
      `${FEED_BASE}/posts`,
      {
        text,
        uploadId: normalizeId(uploadId) || null,
      },
      {
        headers: getAuthHeaders(token),
      }
    );

    return {
      ok: !!response?.data?.ok,
      post: response?.data?.post || null,
    };
  } catch (error) {
    const message = getErrorMessage(error, feedError('No se pudo crear el post.', 'The post could not be created.'));
    const err = new Error(message);
    err.statusCode = getErrorStatus(error);
    err.flags = getErrorFlags(error);
    throw err;
  }
}

async function deletePost({ token, postId } = {}) {
  const safePostId = normalizeId(postId);

  try {
    const response = await authenticatedAxios.delete(`${FEED_BASE}/posts/${safePostId}`, {
      headers: getAuthHeaders(token),
    });

    return {
      ok: !!response?.data?.ok,
      postId: response?.data?.postId || safePostId || '',
      message: response?.data?.message || 'Post eliminado correctamente.',
    };
  } catch (error) {
    const message = getErrorMessage(error, feedError('No se pudo eliminar el post.', 'The post could not be deleted.'));
    const err = new Error(message);
    err.statusCode = getErrorStatus(error);
    err.flags = getErrorFlags(error);
    throw err;
  }
}

async function reportFeedPost({ token, postId, reason = 'other', details = '' } = {}) {
  const safePostId = normalizeId(postId);

  try {
    const response = await authenticatedAxios.post(
      `${FEED_BASE}/posts/${safePostId}/report`,
      {
        reason,
        details,
      },
      {
        headers: getAuthHeaders(token),
      }
    );

    return {
      ok: !!response?.data?.ok,
      alreadyReported: !!response?.data?.alreadyReported,
      postId: response?.data?.postId || safePostId || '',
      reportCount: Number(response?.data?.reportCount || 0),
      message: response?.data?.message || '',
    };
  } catch (error) {
    const message = getErrorMessage(error, feedError('No se pudo reportar la publicación.', 'The post could not be reported.'));
    const err = new Error(message);
    err.statusCode = getErrorStatus(error);
    err.flags = getErrorFlags(error);
    throw err;
  }
}

async function likePost({ token, postId } = {}) {
  const safePostId = normalizeId(postId);

  try {
    const response = await authenticatedAxios.post(
      `${FEED_BASE}/posts/${safePostId}/like`,
      {},
      {
        headers: getAuthHeaders(token),
      }
    );

    return {
      ok: !!response?.data?.ok,
      liked: !!response?.data?.liked,
      postId: response?.data?.postId || safePostId || '',
      likeCount: Number(response?.data?.likeCount || 0),
    };
  } catch (error) {
    const message = getErrorMessage(error, feedError('No se pudo registrar el like.', 'The like could not be recorded.'));
    const err = new Error(message);
    err.statusCode = getErrorStatus(error);
    err.flags = getErrorFlags(error);
    throw err;
  }
}

async function unlikePost({ token, postId } = {}) {
  const safePostId = normalizeId(postId);

  try {
    const response = await authenticatedAxios.delete(`${FEED_BASE}/posts/${safePostId}/like`, {
      headers: getAuthHeaders(token),
    });

    return {
      ok: !!response?.data?.ok,
      liked: !!response?.data?.liked,
      postId: response?.data?.postId || safePostId || '',
      likeCount: Number(response?.data?.likeCount || 0),
    };
  } catch (error) {
    const message = getErrorMessage(error, feedError('No se pudo quitar el like.', 'The like could not be removed.'));
    const err = new Error(message);
    err.statusCode = getErrorStatus(error);
    err.flags = getErrorFlags(error);
    throw err;
  }
}

async function likeComment({ token, commentId } = {}) {
  const safeCommentId = normalizeId(commentId);

  try {
    const response = await authenticatedAxios.post(
      `${FEED_BASE}/comments/${safeCommentId}/like`,
      {},
      {
        headers: getAuthHeaders(token),
      }
    );

    return {
      ok: !!response?.data?.ok,
      liked: !!response?.data?.liked,
      commentId: response?.data?.commentId || safeCommentId || '',
      postId: response?.data?.postId || '',
      likeCount: Number(response?.data?.likeCount || 0),
    };
  } catch (error) {
    const message = getErrorMessage(error, feedError('No se pudo registrar el like de la respuesta.', 'The reply like could not be recorded.'));
    const err = new Error(message);
    err.statusCode = getErrorStatus(error);
    err.flags = getErrorFlags(error);
    throw err;
  }
}

async function unlikeComment({ token, commentId } = {}) {
  const safeCommentId = normalizeId(commentId);

  try {
    const response = await authenticatedAxios.delete(`${FEED_BASE}/comments/${safeCommentId}/like`, {
      headers: getAuthHeaders(token),
    });

    return {
      ok: !!response?.data?.ok,
      liked: !!response?.data?.liked,
      commentId: response?.data?.commentId || safeCommentId || '',
      postId: response?.data?.postId || '',
      likeCount: Number(response?.data?.likeCount || 0),
    };
  } catch (error) {
    const message = getErrorMessage(error, feedError('No se pudo quitar el like de la respuesta.', 'The reply like could not be removed.'));
    const err = new Error(message);
    err.statusCode = getErrorStatus(error);
    err.flags = getErrorFlags(error);
    throw err;
  }
}

async function reportComment({ token, commentId, reason = 'other', details = '' } = {}) {
  const safeCommentId = normalizeId(commentId);

  try {
    const response = await authenticatedAxios.post(
      `${FEED_BASE}/comments/${safeCommentId}/report`,
      {
        reason,
        details,
      },
      {
        headers: getAuthHeaders(token),
      }
    );

    return {
      ok: !!response?.data?.ok,
      alreadyReported: !!response?.data?.alreadyReported,
      commentId: response?.data?.commentId || safeCommentId || '',
      postId: response?.data?.postId || '',
      reportCount: Number(response?.data?.reportCount || 0),
      message: response?.data?.message || '',
    };
  } catch (error) {
    const message = getErrorMessage(error, feedError('No se pudo reportar la respuesta.', 'The reply could not be reported.'));
    const err = new Error(message);
    err.statusCode = getErrorStatus(error);
    err.flags = getErrorFlags(error);
    throw err;
  }
}

async function getComments({ token, postId, limit = 20 } = {}) {
  const safePostId = normalizeId(postId);

  try {
    const response = await authenticatedAxios.get(`${FEED_BASE}/posts/${safePostId}/comments`, {
      headers: getAuthHeaders(token),
      params: { limit },
    });

    return {
      ok: !!response?.data?.ok,
      items: Array.isArray(response?.data?.items) ? response.data.items : [],
    };
  } catch (error) {
    const message = getErrorMessage(error, feedError('No se pudieron cargar los comentarios.', 'Comments could not be loaded.'));
    const err = new Error(message);
    err.statusCode = getErrorStatus(error);
    err.flags = getErrorFlags(error);
    throw err;
  }
}

async function createComment({ token, postId, text } = {}) {
  const safePostId = normalizeId(postId);

  try {
    const response = await authenticatedAxios.post(
      `${FEED_BASE}/posts/${safePostId}/comments`,
      { text },
      {
        headers: getAuthHeaders(token),
      }
    );

    return {
      ok: !!response?.data?.ok,
      comment: response?.data?.comment || null,
    };
  } catch (error) {
    const message = getErrorMessage(error, feedError('No se pudo crear el comentario.', 'The comment could not be created.'));
    const err = new Error(message);
    err.statusCode = getErrorStatus(error);
    err.flags = getErrorFlags(error);
    throw err;
  }
}

async function deleteComment({ token, commentId } = {}) {
  const safeCommentId = normalizeId(commentId);

  try {
    const response = await authenticatedAxios.delete(`${FEED_BASE}/comments/${safeCommentId}`, {
      headers: getAuthHeaders(token),
    });

    return {
      ok: !!response?.data?.ok,
      commentId: response?.data?.commentId || safeCommentId || '',
      postId: response?.data?.postId || '',
      message: response?.data?.message || 'Comentario eliminado correctamente.',
    };
  } catch (error) {
    const message = getErrorMessage(error, feedError('No se pudo eliminar el comentario.', 'The comment could not be deleted.'));
    const err = new Error(message);
    err.statusCode = getErrorStatus(error);
    err.flags = getErrorFlags(error);
    throw err;
  }
}

const feedApi = {
  getFeedBaseUrl,
  buildFeedPostUrl,
  normalizeFeedMode,
  getTimeline,
  uploadFeedImage,
  deleteFeedUpload,
  createPost,
  deletePost,
  reportFeedPost,
  likePost,
  unlikePost,
  likeComment,
  unlikeComment,
  reportComment,
  getComments,
  createComment,
  deleteComment,
};

export {
  getFeedBaseUrl,
  buildFeedPostUrl,
  normalizeFeedMode,
  getTimeline,
  uploadFeedImage,
  deleteFeedUpload,
  createPost,
  deletePost,
  reportFeedPost,
  likePost,
  unlikePost,
  likeComment,
  unlikeComment,
  reportComment,
  getComments,
  createComment,
  deleteComment,
};

export default feedApi;
